<script lang="ts">
export default {
  name: 'LimitPremiumCounter'
}
</script>

<script setup lang="ts">
import { AppState } from '@/store';
import { ComputedRef, computed, inject, ref, watch } from 'vue';
import { Store } from 'vuex';
import { compactNumber, addCommas } from '@/utilities/Number';
import { fabric } from 'fabric';
import { ChevronUp, ChevronDown } from 'lucide-vue-next';
import { formatCurrency } from '@/utilities/Money';

const store = inject<Store<AppState>>('store');

const isExpanded = ref<boolean>(true);

type PolicyRectTypes = fabric.PolicyRect | fabric.CustomPolicyRect;

const isPanelOpen = computed(() => store?.state.towerCanvasState.programsPanelState.show);
const isLimitPremiumCounterEnabled = computed(() => {
  const anyDisplayOptionEnabled = showAggregateLimit.value || showOccurrenceLimit.value || showPremium.value;

  return anyDisplayOptionEnabled && store?.state.towerCanvasState.limitPremiumCounterEnabled;
});

const showAggregateLimit = computed(() => store?.state.towerCanvasState.displayOptions.aggregateLimit.enabled);
const showOccurrenceLimit = computed(() => store?.state.towerCanvasState.displayOptions.occurrenceLimit.enabled);
const showPremium = computed(() => store?.state.towerCanvasState.displayOptions.premium.enabled);

const policies: ComputedRef<PolicyRectTypes[]> = computed(() => {
  const canvasObjects = store?.state.towerCanvasState.canvasObjects || [];
  return canvasObjects.filter((obj): obj is PolicyRectTypes => 
    obj instanceof fabric.PolicyRect || obj instanceof fabric.CustomPolicyRect
  );
});

const totalPremium = computed(() => {
  const premium = policies.value?.reduce((acc, policy) => acc + ((policy as fabric.PolicyRect || policy as fabric.CustomPolicyRect).getPolicyData().policyPremium ?? 0), 0);  
  return addCommas(premium);
});

const limitByCoverageType = computed(() => {
  const map = new Map<string, { occurrence: number, aggregate: number }>();

  for (const policy of policies.value) {
    const coverageType = policy.getPolicyData().coverageType || '(Undefined)';
    const currentData = map.get(coverageType) || { occurrence: 0, aggregate: 0 };

    if (showAggregateLimit.value) {
      currentData.aggregate += policy.getPolicyData().aggregateLimit || 0;
    }
    if (showOccurrenceLimit.value) {
      currentData.occurrence += policy.getPolicyData().occurrenceLimit || 0;
    }

    map.set(coverageType, currentData);
  }

  const formattedMap = new Map<string, string>();
  map.forEach((value, key) => {
    const formattedAggregate = compactNumber(value.aggregate);
    const formattedOccurrence = compactNumber(value.occurrence);
    let formattedValue;
    
    if (key === 'Work Comp') {
      formattedValue = 'Statutory';
    } else {
      formattedValue = showAggregateLimit.value && showOccurrenceLimit.value
        ? `${formattedOccurrence} / ${formattedAggregate}`
        : (showAggregateLimit.value ? formattedAggregate : formattedOccurrence);
      formattedValue = '$' + formattedValue;
    }
    
    formattedMap.set(key, formattedValue);
  });

  return formattedMap;
});

const premiumByCoverageType: ComputedRef<Map<string, number>> = computed(() => {
  const map = new Map<string, number>();
  for (const policy of policies.value) {
    const coverageType = policy.getPolicyData().coverageType || '(Undefined)';
    const premium = policy.getPolicyData().policyPremium || 0;
    map.set(coverageType, (map.get(coverageType) || 0) + premium);
  }
  return map;
});

const limitLabel = computed(() => {
  let label = "Limit";

  if (showOccurrenceLimit.value && showAggregateLimit.value) {
    label += " (Occ/Agg):";
  } else if (showOccurrenceLimit.value) {
    label += " (Occ):";
  } else if (showAggregateLimit.value) {
    label += " (Agg):";
  }

  return label;
});

watch(isLimitPremiumCounterEnabled, (newVal) => {
  if (newVal === true) {
    isExpanded.value = true;
  }
});

</script>

<template>
  <div v-if="isLimitPremiumCounterEnabled" class="tw:rounded-lg tw:flex tw:flex-col tw:col-start-3 tw:h-fit tw:row-start-1 tw:justify-self-end tw:gap-1 tw:m-2 
              tw:pointer-events-auto tw:shadow-lg tw:bg-tower-alt tw:border tw:border-solid tw:border-tower-border"
              :class="isPanelOpen ? ['tw:mr-96'] : ['tw:mr-14']">
    <div class="tw:m-2">
      <!-- Current Visual Label with Toggle -->
      <div class="tw:p-1 tw:flex tw:justify-between tw:items-center">
        <span class="tw:text-sm tw:font-bold">Current Visual:</span>
        <div class="tw:cursor-pointer" @click="isExpanded = !isExpanded">
          <ChevronUp v-if="isExpanded" />
          <ChevronDown v-else />
        </div>
      </div>
  
      <!-- Limit and Premium Tables -->
      <div class="tw:flex tw:flex-col">
        <!-- Limit Table -->
        <table v-if="showAggregateLimit || showOccurrenceLimit" class="tw:min-w-full tw:table-auto">
          <thead>
            <tr>
              <th class="tw:text-left tw:text-sm tw:font-bold tw:underline tw:pr-4">{{ limitLabel }}</th>
            </tr>
          </thead>
          <tbody v-if="isExpanded" aria-label="Limit breakdown" aria-expanded="true">
            <tr v-for="([coverageType, limitValue]) in limitByCoverageType" :key="`limit-${coverageType}`">
              <td class="tw:text-left tw:text-xs">{{ coverageType }}:</td>
              <td class="tw:text-right tw:text-xs tw:font-normal tw:italic">{{ limitValue }}</td>
            </tr>
          </tbody>
        </table>
  
        <!-- Premium Table -->
        <table v-if="showPremium" class="tw:min-w-full tw:table-auto">
          <thead>
            <tr>
              <th class="tw:text-left tw:text-sm tw:font-bold tw:underline tw:pr-4">Premium:</th>
              <th class="tw:text-right tw:text-sm tw:font-bold">${{ totalPremium }}</th>
            </tr>
          </thead>
          <tbody v-if="isExpanded" aria-label="Limit breakdown" aria-expanded="true">
            <tr v-for="([coverageType, premium]) in premiumByCoverageType" :key="`premium-${coverageType}`">
              <td class="tw:text-left tw:text-xs">{{ coverageType }}:</td>
              <td class="tw:text-right tw:text-xs tw:font-normal tw:italic">{{ formatCurrency(premium, true) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
  

